import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    PlusIcon,
    MagnifyingGlassIcon,
    PencilSquareIcon,
    TrashIcon,
    NoSymbolIcon,
    CheckCircleIcon,
    ArrowPathIcon,
    BeakerIcon,
    ShieldCheckIcon
} from '@heroicons/react/24/outline';
import UserModal from '../modals/UserModal'; 
import API_URL from '../config/apiConfig';

const UsersPage = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [currentUser, setCurrentUser] = useState(null);

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [usersPerPage] = useState(10);

    useEffect(() => {
        fetchUsers();
    }, []);

    // --- CONFIGURATION ---
    const getAuthHeaders = () => {
        const token = localStorage.getItem('auth-token');
        return {
            headers: {
                'auth-token': token,
                'Content-Type': 'application/json'
            }
        };
    };

    // --- APPELS API ---

    const fetchUsers = async () => {
        // On ne met le loading que si la liste est vide pour éviter le clignotement
        if (users.length === 0) setLoading(true);
        try {
            const response = await axios.get(`${API_URL}/auth/users`, getAuthHeaders());
            // Gestion robuste : supporte si l'API renvoie un tableau direct ou un objet
            const data = Array.isArray(response.data) ? response.data : response.data.users || [];
            setUsers(data);
        } catch (error) {
            console.error('Erreur chargement utilisateurs:', error);
        } finally {
            setLoading(false);
        }
    };

    // ✅ CHANGEMENT DE STATUT (ACTIF / INACTIF)
    const handleToggleStatus = async (user) => {
        const newStatus = !user.isActive;
        
        try {
            console.log(`Modification statut pour ${user.email} -> ${newStatus}`);
            // 1. Envoi au serveur
            await axios.put(`${API_URL}/auth/users/${user.id}`, {
                isActive: newStatus
            }, getAuthHeaders());
            
            // 2. Rechargement complet pour être sûr de la donnée BDD
            await fetchUsers();

        } catch (error) {
            console.error("Erreur changement statut", error);
            alert("Impossible de modifier le statut : " + (error.response?.data?.msg || error.message));
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("⚠️ ATTENTION : Suppression DÉFINITIVE.\n\nL'historique et les données de ce client seront perdus.\nVoulez-vous continuer ?")) return;

        try {
            await axios.delete(`${API_URL}/auth/users/${id}`, getAuthHeaders());
            setUsers(users.filter(u => u.id !== id));
        } catch (error) {
            console.error("Erreur suppression", error);
            alert("Erreur suppression : " + (error.response?.data?.msg || "Erreur inconnue"));
        }
    };

    const handleSaveUser = async (userData) => {
        try {
            const config = getAuthHeaders();
            
            if (currentUser) {
                // Mise à jour
                await axios.put(`${API_URL}/auth/users/${currentUser.id}`, userData, config);
            } else {
                // Création
                await axios.post(`${API_URL}/auth/create-user`, userData, config);
            }

            setShowModal(false);
            fetchUsers(); // On recharge la liste pour voir les changements (Démo, Admin, etc.)
        } catch (error) {
            console.error('Erreur sauvegarde:', error);
            alert("Erreur lors de la sauvegarde : " + (error.response?.data?.msg || error.message));
        }
    };

    // --- UI ACTIONS ---

    const handleAddUser = () => {
        setCurrentUser(null);
        setShowModal(true);
    };

    const handleEditUser = (user) => {
        setCurrentUser(user);
        setShowModal(true);
    };

    // --- FILTRES & PAGINATION ---

    const filteredUsers = users.filter(user =>
        (user.name && user.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (user.email && user.email.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const indexOfLastUser = currentPage * usersPerPage;
    const indexOfFirstUser = indexOfLastUser - usersPerPage;
    const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
    const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    return (
        <div className="p-6 max-w-7xl mx-auto animate-fade-in">
            
            {/* EN-TÊTE */}
            <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-[#1c2143]">Gestion Utilisateurs</h1>
                    <p className="text-gray-500 text-sm mt-1">Gérez les accès, les quotas et les statuts des clients.</p>
                </div>
                <button
                    onClick={handleAddUser}
                    className="flex items-center px-5 py-2.5 bg-[#Ceaa5b] text-white font-medium rounded-lg shadow-md hover:bg-[#b5954b] transition-all transform hover:-translate-y-0.5 focus:outline-none"
                >
                    <PlusIcon className="w-5 h-5 mr-2" />
                    Nouveau Client
                </button>
            </div>

            {/* BARRE DE RECHERCHE & INFO */}
            <div className="flex justify-between items-center mb-6 bg-white p-4 rounded-lg shadow-sm border border-gray-100">
                <div className="relative w-full max-w-md">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                        <MagnifyingGlassIcon className="w-5 h-5 text-gray-400" />
                    </div>
                    <input
                        type="text"
                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-gray-50 placeholder-gray-400 focus:outline-none focus:bg-white focus:border-[#Ceaa5b] focus:ring-1 focus:ring-[#Ceaa5b] transition-colors sm:text-sm"
                        placeholder="Rechercher par nom ou email..."
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setCurrentPage(1); // Retour page 1 si on cherche
                        }}
                    />
                </div>
                <div className="text-sm text-gray-500 hidden md:block">
                    Total : <span className="font-bold text-[#1c2143]">{filteredUsers.length}</span> utilisateurs
                </div>
            </div>

            {/* TABLEAU */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-[#1c2143] text-white">
                            <tr>
                                <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider">Identité</th>
                                <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider">Rôle & Type</th>
                                <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider">Quota PDF</th>
                                <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider">Statut</th>
                                <th className="px-6 py-4 text-right text-xs font-semibold uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {loading ? (
                                <tr>
                                    <td colSpan="5" className="px-6 py-10 text-center">
                                        <div className="flex justify-center items-center text-[#1c2143]">
                                            <ArrowPathIcon className="w-6 h-6 animate-spin mr-2" />
                                            Chargement des données...
                                        </div>
                                    </td>
                                </tr>
                            ) : currentUsers.length > 0 ? (
                                currentUsers.map((user) => (
                                    <tr key={user.id} className={`hover:bg-gray-50 transition-colors ${!user.isActive ? 'bg-gray-50/50' : ''}`}>
                                        
                                        {/* IDENTITÉ */}
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <div className={`flex-shrink-0 h-10 w-10 rounded-full flex items-center justify-center text-white font-bold ${user.isActive ? 'bg-[#1c2143]' : 'bg-gray-400'}`}>
                                                    {user.name ? user.name.charAt(0).toUpperCase() : '?'}
                                                </div>
                                                <div className="ml-4">
                                                    <div className={`text-sm font-medium ${user.isActive ? 'text-gray-900' : 'text-gray-500 line-through'}`}>
                                                        {user.name}
                                                    </div>
                                                    <div className="text-sm text-gray-500">{user.email}</div>
                                                </div>
                                            </div>
                                        </td>

                                        {/* 👇 2. C'EST ICI QU'ON AFFICHE LE BADGE DÉMO */}
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex flex-col gap-1 items-start">
                                                {/* Badge Super Admin / Admin / Client */}
                                                {user.isSuperAdmin ? (
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-200 gap-1">
                                                        <ShieldCheckIcon className="w-3 h-3" /> Super Admin
                                                    </span>
                                                ) : user.isAdmin ? (
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-200">
                                                        Admin
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100">
                                                        Client
                                                    </span>
                                                )}

                                                {/* Badge Expert */}
                                                {user.isExpert && (
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800 border border-emerald-200 gap-1">
                                                        Expert
                                                    </span>
                                                )}

                                                {/* Badge Démo (si isDemo est vrai) */}
                                                {user.isDemo && (
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 border border-purple-200 gap-1">
                                                        <BeakerIcon className="w-3 h-3" /> Démo
                                                    </span>
                                                )}
                                            </div>
                                        </td>

                                        {/* QUOTA */}
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-900">
                                                <span className="font-bold">{user.pdfMonthlyCount || 0}</span>
                                                <span className="text-gray-400 mx-1">/</span>
                                                {user.pdfMonthlyQuota || 0}
                                            </div>
                                            <div className="w-24 h-1.5 bg-gray-200 rounded-full mt-1 overflow-hidden">
                                                <div
                                                    className={`h-full ${(user.pdfMonthlyCount || 0) >= (user.pdfMonthlyQuota || 0) ? 'bg-red-500' : 'bg-[#Ceaa5b]'}`}
                                                    style={{ width: `${Math.min(((user.pdfMonthlyCount || 0) / (user.pdfMonthlyQuota || 1)) * 100, 100)}%` }}
                                                ></div>
                                            </div>
                                        </td>

                                        {/* STATUT */}
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {user.isSuperAdmin ? (
                                                <span className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-green-50 text-green-700 border border-green-200">
                                                    <CheckCircleIcon className="w-4 h-4"/> Permanent
                                                </span>
                                            ) : (
                                                <button
                                                    onClick={() => handleToggleStatus(user)}
                                                    className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border transition-all shadow-sm
                                                    ${user.isActive
                                                        ? 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100'
                                                        : 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100'}`}
                                                >
                                                    {user.isActive ? (
                                                        <><CheckCircleIcon className="w-4 h-4"/> Actif</>
                                                    ) : (
                                                        <><NoSymbolIcon className="w-4 h-4"/> Inactif</>
                                                    )}
                                                </button>
                                            )}
                                        </td>

                                        {/* ACTIONS */}
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <div className="flex justify-end items-center space-x-3">
                                                {!user.isSuperAdmin && (
                                                    <button
                                                        onClick={() => handleEditUser(user)}
                                                        className="text-gray-400 hover:text-[#Ceaa5b] transition-colors"
                                                        title="Modifier"
                                                    >
                                                        <PencilSquareIcon className="w-5 h-5" />
                                                    </button>
                                                )}
                                                {!user.isAdmin && !user.isSuperAdmin && (
                                                    <button
                                                        onClick={() => handleDelete(user.id)}
                                                        className="text-gray-400 hover:text-red-600 transition-colors"
                                                        title="Supprimer définitivement"
                                                    >
                                                        <TrashIcon className="w-5 h-5" />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                                        Aucun utilisateur trouvé pour cette recherche.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* PAGINATION */}
            {!loading && totalPages > 1 && (
                <div className="flex justify-center mt-8">
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                        {[...Array(totalPages)].map((_, i) => (
                            <button
                                key={i}
                                onClick={() => paginate(i + 1)}
                                className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium
                                ${currentPage === i + 1 
                                    ? 'z-10 bg-[#1c2143] border-[#1c2143] text-white' 
                                    : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'}`}
                            >
                                {i + 1}
                            </button>
                        ))}
                    </nav>
                </div>
            )}

            {/* MODAL */}
            {showModal && (
                <UserModal
                    user={currentUser}
                    onClose={() => setShowModal(false)}
                    onSave={handleSaveUser}
                />
            )}
        </div>
    );
};

export default UsersPage;